// IndexedDB-based audio caching for offline playback
const DB_NAME = 'briefcast-audio-cache';
const DB_VERSION = 1;
const STORE_NAME = 'audio-files';
const CACHE_EXPIRY_DAYS = 7;

interface CachedAudio {
  masterBriefId: string;
  audioBlob: Blob;
  cachedAt: number;
  durationSeconds?: number;
}

// In-memory blob URL cache to avoid recreating URLs
const blobUrlCache = new Map<string, string>();

// Track ongoing downloads to prevent duplicates
const downloadingSet = new Set<string>();

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'masterBriefId' });
      }
    };
  });
}

export async function getCachedAudioUrl(masterBriefId: string): Promise<string | null> {
  // Check in-memory cache first
  const memoryUrl = blobUrlCache.get(masterBriefId);
  if (memoryUrl) {
    return memoryUrl;
  }

  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(masterBriefId);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cached = request.result as CachedAudio | undefined;
        
        if (!cached) {
          resolve(null);
          return;
        }
        
        // Check if expired
        const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        if (Date.now() - cached.cachedAt > expiryTime) {
        // Expired, clean up
          removeCachedAudio(masterBriefId).catch(console.error);
          resolve(null);
          return;
        }
        
        // Create blob URL and cache it in memory
        const blobUrl = URL.createObjectURL(cached.audioBlob);
        blobUrlCache.set(masterBriefId, blobUrl);
        console.log('[AudioCache] Loaded from IndexedDB:', masterBriefId);
        resolve(blobUrl);
      };
    });
  } catch (error) {
    console.error('[AudioCache] Error reading from IndexedDB:', error);
    return null;
  }
}

export async function cacheAudio(
  masterBriefId: string, 
  audioBlob: Blob,
  durationSeconds?: number
): Promise<string> {
  try {
    const db = await openDatabase();
    
    const cachedAudio: CachedAudio = {
      masterBriefId,
      audioBlob,
      cachedAt: Date.now(),
      durationSeconds,
    };
    
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(cachedAudio);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
    
    // Create blob URL and cache in memory
    const blobUrl = URL.createObjectURL(audioBlob);
    blobUrlCache.set(masterBriefId, blobUrl);
    
    console.log('[AudioCache] Cached audio:', masterBriefId, `(${(audioBlob.size / 1024 / 1024).toFixed(2)}MB)`);
    return blobUrl;
  } catch (error) {
    console.error('[AudioCache] Error caching audio:', error);
    throw error;
  }
}

export async function downloadAudioInBackground(
  masterBriefId: string,
  signedUrl: string,
  durationSeconds?: number
): Promise<string> {
  // Check if already downloading
  if (downloadingSet.has(masterBriefId)) {
    console.log('[AudioCache] Already downloading:', masterBriefId);
    // Wait for existing download by polling the cache
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(async () => {
        const cachedUrl = await getCachedAudioUrl(masterBriefId);
        if (cachedUrl) {
          clearInterval(checkInterval);
          resolve(cachedUrl);
        }
        if (!downloadingSet.has(masterBriefId)) {
          clearInterval(checkInterval);
          reject(new Error('Download was cancelled'));
        }
      }, 500);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Download timeout'));
      }, 5 * 60 * 1000);
    });
  }

  // Check if already cached
  const existingUrl = await getCachedAudioUrl(masterBriefId);
  if (existingUrl) {
    return existingUrl;
  }

  downloadingSet.add(masterBriefId);
  console.log('[AudioCache] Starting background download:', masterBriefId);

  try {
    const response = await fetch(signedUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to download audio: ${response.status}`);
    }
    
    const audioBlob = await response.blob();
    const blobUrl = await cacheAudio(masterBriefId, audioBlob, durationSeconds);
    
    console.log('[AudioCache] Background download complete:', masterBriefId);
    return blobUrl;
  } finally {
    downloadingSet.delete(masterBriefId);
  }
}

export function isCached(masterBriefId: string): boolean {
  return blobUrlCache.has(masterBriefId);
}

export function isDownloading(masterBriefId: string): boolean {
  return downloadingSet.has(masterBriefId);
}

export function getBlobUrl(masterBriefId: string): string | null {
  return blobUrlCache.get(masterBriefId) || null;
}

export async function removeCachedAudio(masterBriefId: string): Promise<void> {
  // Revoke blob URL if exists
  const blobUrl = blobUrlCache.get(masterBriefId);
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl);
    blobUrlCache.delete(masterBriefId);
  }
  
  try {
    const db = await openDatabase();
    
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(masterBriefId);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (error) {
    console.error('[AudioCache] Error deleting from cache:', error);
  }
}

export async function clearExpiredCache(): Promise<void> {
  try {
    const db = await openDatabase();
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const now = Date.now();
    
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();
    
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        const cached = cursor.value as CachedAudio;
        if (now - cached.cachedAt > expiryTime) {
          console.log('[AudioCache] Clearing expired:', cached.masterBriefId);
          
          // Revoke blob URL if in memory
          const blobUrl = blobUrlCache.get(cached.masterBriefId);
          if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
            blobUrlCache.delete(cached.masterBriefId);
          }
          
          cursor.delete();
        }
        cursor.continue();
      }
    };
    
    console.log('[AudioCache] Expired cache cleanup complete');
  } catch (error) {
    console.error('[AudioCache] Error clearing expired cache:', error);
  }
}
